import { FC, useEffect, useRef } from "react";
import { ReduxState, toggleListItemExpand, useListContext, useRecordContext, useResourceContext } from "ra-core";
import FileCataInfoEdit from "./FileCataInfoEdit";
import { Box, Button } from "@mui/material";
import { FileResponseDto, PlainFileScanResponseDto, UpdateFileInput } from "@/gen/api/securefile";
import { FileCataInfoEditContext, useFileCataInfoEditController } from "./FileCataInfoEditContext";
import CreateOrEdit from "@/controller/CreateOrEdit";
import WithSave from "@/controller/WithSave";
import { useDispatch, useSelector } from "react-redux";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_DATA_CATALOG } from "@/constants";


const RowExpandFileCataEdit: FC = () => {
  const currRes = useResourceContext()
  const record = useRecordContext()
  const dto: FileResponseDto|PlainFileScanResponseDto = record as any
  const dispatch = useDispatch()
  const { refetch } = useListContext()
  const hasClssPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const hasEditPermit = useHasPermit([MANAGE_DATA_CATALOG])

  const ctrl = useFileCataInfoEditController(dto as any)
  const {
    dirty,
    getEffectClssItem,
    getEffectTags,
  } = ctrl

  const dtoId: number = (dto as any).fileId ? (dto as any).fileId : (dto as any).entryId
  
  const handleSave = (save: (record:any,callbacks?:any)=>void) => () => {
    if (!hasClssPermit && !hasEditPermit) { return }
    if (!dirty) { return }
    const tags = hasEditPermit ? getEffectTags().map(x => x?.tagId!) : dto.tags
    const clss = hasClssPermit ? getEffectClssItem() : dto
    const payload : UpdateFileInput = {
      fileId: dtoId,
      tags,
      ...(clss 
        ? {
          primaryClassification: clss.primaryClassification,
          secondaryClassificationId: clss.secondaryClassificationId,
        }
        : {
          primaryClassification: null,
          secondaryClassificationId: null,
        }),
    }
    save(payload, {
      onSuccess: () => {
        refetch()
        closeExpanded()
      }
    })
  }
  
  const closeExpanded = () => {
    dispatch(toggleListItemExpand(currRes, dtoId))
  }
  
  const dialogRef = useRef<HTMLElement|null>(null)
  useEffect(() => {
    setTimeout(() => {
      dialogRef.current?.focus()
    })
  }, [])
  const activeInstancesCountRes = useSelector<ReduxState>(store => !!store.admin.resources['secure-files-instances-count'])
  if (!activeInstancesCountRes) { return null }
  return (
    <Box role="dialog">
      <span tabIndex={-1} ref={dialogRef} className="sr-only">Field Edit Region for {`${dto.fileName}`}</span>
      <FileCataInfoEditContext.Provider value={ctrl}>
        <FileCataInfoEdit />
      </FileCataInfoEditContext.Provider>
      <Box 
        sx={{
          mb: -2,
          mx: -2,
          py: '10px',
          px: 5,
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.default',
          '& button': {
            width: 96,
            mr: 2,
          },
        }}
      >
        {(hasClssPermit || hasEditPermit) &&
          <CreateOrEdit record={record} id={record?.id} resource={currRes} basePath="">
            <WithSave 
              render={({save, saving}) =>
                <Button
                  disabled={saving || !dirty}
                  variant="contained" 
                  size="small" 
                  color="primary"
                  onClick={handleSave(save)}
                >
                  Save
                </Button>
              }
            />
          </CreateOrEdit>}
        <Button
          variant="outlined" 
          size="small" 
          color="primary"
          onClick={closeExpanded}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  )
}

export default RowExpandFileCataEdit;
